import axios from 'axios';
import { Config, getToken } from './config';

/**
 * Interface para os dados do perfil do usuário
 */
export interface UserProfile {
  id: number;
  nome: string;
  email: string;
  telefone?: string;
  tipo: 'admin' | 'supervisor' | 'operador';
  cargo?: string;
  empresa_id: number;
  ativo: boolean;
  foto_perfil_url?: string;
  criado_em: string;
  atualizado_em: string;
}

/**
 * Interface para atualização do perfil
 */
export interface UpdateProfileData {
  nome?: string;
  telefone?: string;
  cargo?: string;
}

/**
 * Interface para alteração de senha
 */
export interface ChangePasswordData {
  senhaAtual: string;
  novaSenha: string;
  confirmarSenha: string;
}

/**
 * Interface para preferências de privacidade
 */
export interface PrivacyPreferences {
  notificacoes_email?: boolean;
  notificacoes_sistema?: boolean;
  perfil_visivel?: boolean;
  compartilhar_dados?: boolean;
}

/**
 * Classe de serviço para gerenciamento do perfil do usuário
 */
class ProfileService {
  private API_URL = Config.API_BASE_URL;

  /**
   * Obtém os dados do perfil do usuário autenticado
   * @returns Promise com os dados do perfil
   */
  async getProfile(): Promise<UserProfile> {
    const token = getToken();

    const response = await axios.get(`${this.API_URL}/usuarios/perfil`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000
    });

    return response.data;
  }

  /**
   * Atualiza os dados do perfil do usuário autenticado
   * @param data - Dados a serem atualizados
   * @returns Promise com os dados atualizados
   */
  async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
    const token = getToken();
    const response = await axios.put(
      `${this.API_URL}/usuarios/perfil`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  /**
   * Altera a senha do usuário autenticado
   * @param data - Dados para alteração de senha (senha atual e nova senha)
   * @returns Promise com a resposta da API
   */
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const token = getToken();
    const response = await axios.post(
      `${this.API_URL}/auth/change-password`,
      {
        senhaAtual: data.senhaAtual,
        novaSenha: data.novaSenha
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  /**
   * Obtém as preferências de privacidade do usuário
   * @returns Promise com as preferências
   */
  async getPrivacyPreferences(): Promise<PrivacyPreferences> {
    // Por enquanto retorna valores padrão
    // TODO: Implementar endpoint no backend se necessário
    return {
      notificacoes_email: true,
      notificacoes_sistema: true,
      perfil_visivel: true,
      compartilhar_dados: false
    };
  }

  /**
   * Atualiza as preferências de privacidade do usuário
   * @param preferences - Preferências a serem atualizadas
   * @returns Promise com as preferências atualizadas
   */
  async updatePrivacyPreferences(preferences: PrivacyPreferences): Promise<PrivacyPreferences> {
    // Por enquanto apenas retorna as preferências recebidas
    // TODO: Implementar endpoint no backend para persistir
    return preferences;
  }
}

// Exportar instância única do serviço
const profileService = new ProfileService();
export default profileService;
